import { useState } from 'react';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { performanceForRole, type PerformanceInput } from './Performance.transform';
import type { RoleDashboard } from './Scene.transform';
import { OperatorLanding } from './OperatorLanding';
import { OpsSupervisorLanding } from './OpsSupervisorLanding';
import { SeniorMetLanding, type SeniorMetTabId } from './SeniorMetLanding';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { ControlMetLanding } from './ControlMetLanding';
import { ControlRoomLanding } from './ControlRoomLanding';
import type { CmetTabId } from './ControlMetLanding.transform';
import { SuptLanding } from './SuptLanding';
import { MaintenanceLanding } from './MaintenanceLanding';
import { GmLanding } from './GmTabs';

type RoleLandingDashboardProps = {
  roleId: PlantRoleId;
  dashboard: RoleDashboard;
  projectSlug: string;
  performanceInput: PerformanceInput;
  sessionState: SessionState;
  advisory: AdvisorySessionState;
  onOpenCase: (caseId: string) => void;
};

/**
 * The role landing dispatch (#795). Every `PlantRoleId` has its own landing, so
 * the switch is exhaustive: the `never` default makes `tsc` fail the moment a
 * role joins the union without a landing, rather than rendering an empty page.
 * The old catch-all "shared §8 frame" body was unreachable — `PerformanceRoleId`
 * is a subset of `PlantRoleId` and every member returned early above it.
 */
export function RoleLandingDashboard({
  roleId,
  dashboard,
  projectSlug,
  performanceInput,
  sessionState,
  advisory,
  onOpenCase,
}: RoleLandingDashboardProps) {
  const [cmetTab, setCmetTab] = useState<CmetTabId>('kpi');
  const [seniorMetTab, setSeniorMetTab] = useState<SeniorMetTabId>('health');

  const { can } = useRole();
  const showMoney = can('money:view');

  switch (roleId) {
    case 'senior-metallurgist':
      return (
        <SeniorMetLanding
          projectSlug={projectSlug}
          sessionState={sessionState}
          advisory={advisory}
          onOpenCase={onOpenCase}
          tab={seniorMetTab}
          onTabChange={setSeniorMetTab}
        />
      );

    case 'ops-supervisor':
      return <OpsSupervisorLanding state={sessionState} projectSlug={projectSlug} onOpenCase={onOpenCase} />;

    case 'operator':
      return <OperatorLanding state={sessionState} projectSlug={projectSlug} onOpenCase={onOpenCase} />;

    case 'superintendent':
      return <SuptLanding state={sessionState} projectSlug={projectSlug} />;

    case 'cro':
      return (
        <ControlRoomLanding
          state={sessionState}
          projectSlug={projectSlug}
          performanceInput={performanceInput}
          table={dashboard.tables.find((t) => t.caption.includes('process variables'))}
          onOpenCase={onOpenCase}
        />
      );

    case 'maintenance':
      return <MaintenanceLanding state={sessionState} projectSlug={projectSlug} />;

    case 'metallurgist':
      return (
        <ControlMetLanding
          state={sessionState}
          projectSlug={projectSlug}
          onOpenCase={onOpenCase}
          tab={cmetTab}
          onTabChange={setCmetTab}
        />
      );

    case 'gm': {
      const model = performanceForRole('gm', performanceInput, sessionState, { showMoney });
      return (
        <GmLanding
          state={sessionState}
          model={model}
          projectSlug={projectSlug}
          byAreaRows={model.byAreaRows ?? []}
        />
      );
    }

    default: {
      const exhaustive: never = roleId;
      return exhaustive;
    }
  }
}
